import { tokenManager } from './tokenManager';
import { getViewScopeBrandForRequest } from './viewScope';
import { apiUrl } from './apiUrl';

/** 登入頁路徑（authProvider 登出 / check 失敗都導向這裡）。 */
const LOGIN_PATH = "/manage";

/** 只導向一次，避免 Promise.all 併發呼叫時重複觸發導航。 */
let sessionExpiredHandled = false;

/**
 * 工作階段失效統一收斂點 — session（access + refresh token）都無法續期，
 * 或後端回 HTTP 401 時呼叫。清掉本地 token 並導回登入頁。
 *
 * Why：dashboard 等頁面有 30s 自動輪詢（`authFetch` → `/api/admin/*`）。
 * 過期後若不攔截，`getToken()` refresh 失敗回 null，`authFetch` 仍會不帶
 * Authorization 打出去 → 後端 401 → 前端 catch 吞掉 → 30s 後再打，形成
 * 「過期 session 無限輪詢 401」（實測單一內部 IP 累積 2 萬+ 筆 401）。
 * 在此統一清 token + 硬導航到登入頁：硬導航會卸載整個 SPA（含所有輪詢
 * timer），從根本切斷輪詢迴圈，且與 authProvider.logout 的 redirectTo 一致。
 */
function handleSessionExpired(): void {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  tokenManager.clear();
  if (typeof window !== "undefined") {
    const path = window.location?.pathname ?? "";
    // 已在登入頁就不再導（避免登入頁自身打 API 觸發迴圈導航）。
    if (!path.startsWith(LOGIN_PATH)) {
      window.location.assign(LOGIN_PATH);
    }
  }
}

/** 給呼叫端在早退時解析用的合成 401 Response（body 對齊後端 { code, msg, data }）。 */
function makeUnauthorizedResponse(): Response {
  return new Response(
    JSON.stringify({ code: 401, msg: "認證過期，請重新登入", data: null }),
    { status: 401, headers: { "Content-Type": "application/json" } },
  );
}

/**
 * 帶認證的 fetch 封裝
 * - 自動帶上 Authorization header（透過 tokenManager）
 * - token 過期時自動 refresh 後重試
 * - session 徹底失效（refresh 也失敗）或後端回 401 → 統一清 token + 導回登入，
 *   從根本切斷「過期 session 無限輪詢 401」（見 handleSessionExpired）
 * - 預設 Content-Type: application/json
 * - 依「檢視範圍」帶 X-Brand（單品牌檢視送 `X-Brand:<brand>`，聚合檢視省略）
 *
 * X-Brand 純粹是「讀取視角」通道：後端 admin 讀取端點用它過濾要看哪個品牌，
 * 聚合模式刻意省略 header（不送 'all'——'all' 會被當品牌字串過濾出 0 rows）。
 * 寫入絕不靠 X-Brand（寫入走顯式 targetBrand / X-Target-Brand），兩通道不交叉。
 *
 * 所有頁面的 API 呼叫都應使用此函數，取代手動 localStorage.getItem('token')
 */
export async function authFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const token = await tokenManager.getToken();

  // session 徹底失效（無 token 或 refresh 失敗）→ 不再打無意義的無認證請求
  // （必然 401），直接收斂：清 token + 導回登入，回合成 401 讓呼叫端優雅結束。
  if (!token) {
    handleSessionExpired();
    return makeUnauthorizedResponse();
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  headers["Authorization"] = `Bearer ${token}`;
  // 單品牌檢視才送 X-Brand；聚合檢視（brandForRequest === null）省略。
  const viewBrand = getViewScopeBrandForRequest();
  if (viewBrand) {
    headers["X-Brand"] = viewBrand;
  }

  // S3 BO 的 apiUrl 會以啟動時載入的 runtime API origin 組成請求 URL。
  const response = await fetch(apiUrl(url), { ...options, headers });

  // 後端 HTTP 401（token 被伺服端撤銷 / 失效）→ 同樣收斂，切斷後續輪詢。
  if (response.status === 401) {
    handleSessionExpired();
  }

  return response;
}
